/**
 * Track-actions popover. A long-press on the ownship boat, or on the
 * orientation (north-up / head-up / course-up) control, opens this small popover
 * under the press. It's organised into submenus rather than one long flat list
 * (feedback fb-mrvrj94v-1): the root shows a couple of groups, and tapping one
 * drills into just that group's actions with a Back header. Keeping one short
 * panel on screen at a time also stops the card running off a short (landscape
 * phone) viewport, the other half of that report.
 *
 *   Track group
 *     - Stop track    — pause recording (the breadcrumb stays on the chart, we
 *                       just stop adding to it), because sometimes we want to
 *                       hold the track we have without laying down more.
 *     - Reset track   — wipe the current track but leave recording as it was, so
 *                       it clears the chart without changing whether we're
 *                       logging. Recording keeps recording, paused stays paused.
 *     - Save track    — snapshot the current track into the on-device library so
 *                       it survives a reload (handled by the saved-tracks store).
 *
 *   Boat group — quick shortcuts to our own vessel's setup: the boat profile
 *     (draught, air draught, length, cruise speed), the own-ship vectors, and the
 *     look-ahead danger cone. These just jump to the existing menu controls
 *     rather than duplicating them, so there's one source of truth for each.
 *
 * Positioning: we measure the card once it's rendered and clamp it fully inside
 * the viewport, so it can't hang off an edge whichever control opened it (the
 * boat, or the orientation icon up in the top bar). A tall card on a short
 * viewport caps its height and scrolls rather than overflowing.
 *
 * It reuses the existing store actions rather than reinventing any track logic:
 * recording is gated by the sim store's recordTrack flag (read in GnssIngest),
 * the live points live in the chart-plotter store, and Save hands off to the
 * saved-tracks library.
 */
import { anchorPositionNow } from '@/ownship/anchorDrop';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useShellStore } from '@/store/shell';
import { useChartPlotterStore, PRIMARY_TRACK_ID } from '@/store';
import { useSimStore } from '@/store/sim';
import { useSavedTracksStore } from '@/store/savedTracks';
import { useAnchorWatchStore } from '@/store/anchorWatch';
import { haptic } from '@/ui/haptics';

const CARD_W = 224;
const MARGIN = 12;

type MenuView = 'root' | 'track' | 'boat';

/**
 * Clamp the popover fully inside the viewport. Anchored under the press, then
 * pulled back inside both axes; on a short viewport the card caps its height (and
 * scrolls) rather than hanging off the bottom. Pure, so the on-screen behaviour
 * can be tested without a DOM (feedback fb-mrvrj94v-1: off-screen from the top-bar
 * orientation icon).
 */
export function clampPopover(
  anchorX: number,
  anchorY: number,
  w: number,
  h: number,
  vw: number,
  vh: number,
): { left: number; top: number; maxHeight: number } {
  const maxHeight = vh - 2 * MARGIN;
  const usedH = Math.min(h, maxHeight);
  // Nudge up a touch so a finger on the boat doesn't sit over the card.
  const left = Math.max(MARGIN, Math.min(vw - w - MARGIN, anchorX - w / 2));
  const top = Math.max(MARGIN, Math.min(vh - usedH - MARGIN, anchorY - 20));
  return { left, top, maxHeight };
}

export function TrackActionsPopover() {
  const trackMenu = useShellStore((s) => s.trackMenu);
  const closeTrackMenu = useShellStore((s) => s.closeTrackMenu);
  const showToast = useShellStore((s) => s.showToast);
  const recording = useSimStore((s) => s.recordTrack);
  // The anchor's current state, so the Drop-anchor entry can show as already down
  // (and disable itself) rather than dropping a second time on top of the first.
  const anchor = useAnchorWatchStore((s) => s.anchor);

  // Which panel is showing: the group list, or one group's actions. Reset to the
  // root every time the popover reopens so it always starts at the top level.
  const [view, setView] = useState<MenuView>('root');
  useEffect(() => {
    if (trackMenu) setView('root');
  }, [trackMenu]);

  // A light tick the moment the popover opens — the long-press activation buzz,
  // fired here so both entry points (the boat and the orientation control) feel
  // the same however they opened it.
  useEffect(() => {
    if (trackMenu) haptic('tick');
  }, [trackMenu]);

  // Measure the rendered card and clamp it fully on screen. Done after layout
  // (and re-run when the panel changes height) so a short viewport or a control
  // near an edge can't leave the card hanging off. Falls back to a sensible
  // estimate before the first measure so there's no visible jump.
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number; maxHeight: number } | null>(null);
  useLayoutEffect(() => {
    if (!trackMenu) return;
    const card = cardRef.current;
    const h = card ? card.offsetHeight : 320;
    const w = card ? card.offsetWidth : CARD_W;
    setPos(clampPopover(trackMenu.x, trackMenu.y, w, h, window.innerWidth, window.innerHeight));
  }, [trackMenu, view]);

  if (!trackMenu) return null;

  function stopTrack() {
    haptic('commit');
    useSimStore.getState().setRecordTrack(false);
    showToast('Track recording paused');
    closeTrackMenu();
  }

  function resetTrack() {
    // Clear the current track only. We deliberately leave the recordTrack flag
    // alone, so whatever we were doing before carries on: recording keeps
    // recording, paused stays paused.
    haptic('commit');
    useChartPlotterStore.getState().clearTracks();
    showToast('Track cleared');
    closeTrackMenu();
  }

  function saveTrack() {
    const points = useChartPlotterStore.getState().tracks[PRIMARY_TRACK_ID]?.points ?? [];
    const id = useSavedTracksStore.getState().saveTrack(points);
    if (id) {
      haptic('commit');
      showToast('Track saved to the device');
    } else {
      // Fewer than two points is nothing worth keeping; say so rather than
      // silently doing nothing.
      showToast('Not enough track to save yet');
    }
    closeTrackMenu();
  }

  // Drop the anchor right at the boat and arm the watch. This reuses the anchor
  // store's dropAnchor — the very same action the ready-anchor HUD button and the
  // AnchorWatch control's "Drop here" call — so there's one definition of what
  // dropping the anchor means and we don't reimplement any anchor logic here.
  // Setting the anchor position is what arms the watch (an anchor that's down IS
  // the watch running), which is why one call does both.
  function dropAnchorHere() {
    const pos = useChartPlotterStore.getState().ownship.position;
    if (!pos) {
      // No fix yet means we've nowhere to drop it, so say so rather than planting
      // the anchor at a stale or empty position.
      showToast('Anchor — no vessel position yet');
      return;
    }
    haptic('commit');
    // The bow, not the aerial: the anchor leaves the bow roller and the swing
    // circle is centred on where it went in.
    useAnchorWatchStore.getState().dropAnchor(anchorPositionNow(pos) ?? pos);
    showToast('Anchor dropped at the boat — watch armed');
    closeTrackMenu();
  }

  // Shortcuts to our own boat's setup. These close the popover and open the
  // existing menu control, so there's one source of truth for each setting.
  function openBoatSetup() {
    haptic('tick');
    closeTrackMenu();
    useShellStore.getState().openMenuAt('boat', 'boat-profile');
  }

  // Own-ship vector length lives with the boat settings; the "Vectors" shortcut
  // jumps straight to it rather than duplicating the control here.
  function openVectors() {
    haptic('tick');
    closeTrackMenu();
    useShellStore.getState().openMenuAt('boat', 'own-vector');
  }

  function openDangerCone() {
    haptic('tick');
    closeTrackMenu();
    useShellStore.getState().openMenuAt('safety', 'grounding-alarm');
  }

  function goto(next: MenuView) {
    haptic('tick');
    setView(next);
  }

  return (
    <div
      className="acp-trackmenu-wrap"
      onClick={closeTrackMenu}
      role="dialog"
      aria-label="Track and boat actions"
    >
      <div
        ref={cardRef}
        className="acp-trackmenu"
        style={{
          left: pos?.left ?? trackMenu.x - CARD_W / 2,
          top: pos?.top ?? trackMenu.y - 20,
          width: CARD_W,
          maxHeight: pos?.maxHeight,
          overflowY: 'auto',
          // Hide the card until it's been measured and clamped, so it never
          // flashes in an off-screen spot before settling.
          visibility: pos ? 'visible' : 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {view === 'root' && (
          <>
            <div className="acp-trackmenu-title">Actions</div>
            <button type="button" className="acp-trackmenu-item" onClick={() => goto('track')}>
              <span className="ico" aria-hidden>
                〰️
              </span>
              <span className="acp-trackmenu-text">
                <span className="acp-trackmenu-label">Track</span>
                <span className="acp-trackmenu-sub">{recording ? 'Recording' : 'Paused'} · stop, reset, save</span>
              </span>
              <span className="acp-trackmenu-chev" aria-hidden>›</span>
            </button>
            <button type="button" className="acp-trackmenu-item" onClick={() => goto('boat')}>
              <span className="ico" aria-hidden>
                ⛵
              </span>
              <span className="acp-trackmenu-text">
                <span className="acp-trackmenu-label">Boat</span>
                <span className="acp-trackmenu-sub">Setup, vectors, danger cone</span>
              </span>
              <span className="acp-trackmenu-chev" aria-hidden>›</span>
            </button>
          </>
        )}

        {view === 'track' && (
          <>
            <button type="button" className="acp-trackmenu-back" onClick={() => goto('root')}>
              <span aria-hidden>‹</span> Back
            </button>
            <div className="acp-trackmenu-title">
              Track
              <span className="acp-trackmenu-state">{recording ? 'recording' : 'paused'}</span>
            </div>
            <button type="button" className="acp-trackmenu-item" onClick={stopTrack} disabled={!recording}>
              <span className="ico" aria-hidden>
                ⏸
              </span>
              <span className="acp-trackmenu-text">
                <span className="acp-trackmenu-label">Stop track</span>
                <span className="acp-trackmenu-sub">Pause recording, keep the track so far</span>
              </span>
            </button>
            <button type="button" className="acp-trackmenu-item" onClick={resetTrack}>
              <span className="ico" aria-hidden>
                ⌫
              </span>
              <span className="acp-trackmenu-text">
                <span className="acp-trackmenu-label">Reset track</span>
                <span className="acp-trackmenu-sub">Clear the track and start a fresh one</span>
              </span>
            </button>
            <button type="button" className="acp-trackmenu-item" onClick={saveTrack}>
              <span className="ico" aria-hidden>
                💾
              </span>
              <span className="acp-trackmenu-text">
                <span className="acp-trackmenu-label">Save track</span>
                <span className="acp-trackmenu-sub">Keep a copy on the device</span>
              </span>
            </button>
          </>
        )}

        {view === 'boat' && (
          <>
            <button type="button" className="acp-trackmenu-back" onClick={() => goto('root')}>
              <span aria-hidden>‹</span> Back
            </button>
            <div className="acp-trackmenu-title">Boat</div>
            <button
              type="button"
              className="acp-trackmenu-item"
              onClick={dropAnchorHere}
              disabled={anchor != null}
            >
              <span className="ico" aria-hidden>
                ⚓
              </span>
              <span className="acp-trackmenu-text">
                <span className="acp-trackmenu-label">Drop anchor</span>
                <span className="acp-trackmenu-sub">
                  {anchor != null ? 'Anchor already down' : 'Set it at the boat, arm the watch'}
                </span>
              </span>
            </button>
            <button type="button" className="acp-trackmenu-item" onClick={openBoatSetup}>
              <span className="ico" aria-hidden>
                ⛵
              </span>
              <span className="acp-trackmenu-text">
                <span className="acp-trackmenu-label">Boat details &amp; setup</span>
                <span className="acp-trackmenu-sub">Draught, length, cruise speed</span>
              </span>
            </button>
            <button type="button" className="acp-trackmenu-item" onClick={openVectors}>
              <span className="ico" aria-hidden>
                ➡️
              </span>
              <span className="acp-trackmenu-text">
                <span className="acp-trackmenu-label">Vectors</span>
                <span className="acp-trackmenu-sub">Course &amp; heading vector length</span>
              </span>
            </button>
            <button type="button" className="acp-trackmenu-item" onClick={openDangerCone}>
              <span className="ico" aria-hidden>
                ⛰️
              </span>
              <span className="acp-trackmenu-text">
                <span className="acp-trackmenu-label">Danger cone &amp; look-ahead</span>
                <span className="acp-trackmenu-sub">Cone on/off, distance or time</span>
              </span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
